/**
 * Maps a server-validated `create_data_chart` spec onto ChartCard props.
 *
 * The spec already speaks ChartCard vocabulary (types, axes, metric types), so
 * nothing is inferred here from field names or sample values.
 */

import type {
  CartesianChartBaseDefinition,
  ChartCardChartProps,
  ChartCardProps,
  ChartDataRow,
  ChartDimensionValuesVariable,
  ChartMetric,
  Dimension
} from 'shared/components/ChartCard/types'
import { type CopilotDataChart, type DataChartMetric, type DataChartSpec } from './dataChartTypes'
import {
  buildDimensionDescriptions,
  buildScatterSeriesDimension,
  buildTypedMetric,
  COPILOT_SCATTER_SERIES_FIELD,
  COPILOT_SCATTER_SERIES_VALUE,
  normalizeAgentDateValue
} from './inferDataDefinition'

type Row = ChartDataRow<string>
type SecondAxis = ChartCardChartProps<string, Row>['secondAxis']

const STACK_ID = 'copilot-data-chart-stack'

export const COPILOT_CHART_CUSTOMISATIONS = {
  allowChangeCardType: false,
  allowExport: true,
  chartSize: {
    height: 280
  }
} as const

const toFiniteNumber = (value: unknown): number | null => {
  if (value == null || (typeof value === 'string' && value.trim().length === 0)) return null
  const numeric = Number(value)
  return Number.isFinite(numeric) ? numeric : null
}

const metricFields = (metrics: DataChartMetric[]): string[] => metrics.map((metric) => metric.field)

const normalizeRows = ({ spec, data }: CopilotDataChart): Row[] => {
  const numericFields = metricFields([...spec.metrics, ...spec.secondMetrics])
  const injectScatterSeries = spec.type === 'scatter' && spec.seriesDimension == null

  return data.map((row) => {
    const normalized: Row = {}
    const { dimension, seriesDimension } = spec

    if (dimension != null && row[dimension.field] != null) {
      const value = String(row[dimension.field])
      normalized[dimension.field] = dimension.type === 'date' ? normalizeAgentDateValue(value) : value
    }
    if (seriesDimension != null && row[seriesDimension.field] != null) {
      normalized[seriesDimension.field] = String(row[seriesDimension.field])
    }
    numericFields.forEach((field) => {
      normalized[field] = toFiniteNumber(row[field])
    })
    if (injectScatterSeries) {
      normalized[COPILOT_SCATTER_SERIES_FIELD] = COPILOT_SCATTER_SERIES_VALUE
    }

    return normalized
  })
}

/**
 * ChartCard ranks categories by the untransposed metric id, which transposing across
 * `seriesDimension` removes from its rows, so every category would rank as zero. Such charts
 * are ranked here by the category total across series, and ChartCard keeps that row order.
 */
const ranksCategoriesHere = (spec: DataChartSpec): boolean =>
  spec.seriesDimension != null && (spec.sort === 'metricsAsc' || spec.sort === 'metricsDesc')

const rankRowsByCategoryTotal = (rows: Row[], spec: DataChartSpec): Row[] => {
  const dimensionField = spec.dimension?.field
  if (dimensionField == null) return rows

  // dataChartSpecSchema allows a series_dimension chart exactly one metric, so the ranking never goes stale.
  const metricField = spec.metrics[0].field
  const totals = new Map<string, number>()
  rows.forEach((row) => {
    const category = String(row[dimensionField])
    const value = row[metricField]
    totals.set(category, (totals.get(category) ?? 0) + (typeof value === 'number' ? value : 0))
  })

  const direction = spec.sort === 'metricsAsc' ? 1 : -1
  const total = (row: Row): number => totals.get(String(row[dimensionField])) ?? 0
  return [...rows].sort((a, b) => direction * (total(a) - total(b)))
}

const buildDimensions = (spec: DataChartSpec, rows: Row[]): Array<Dimension<'props', string, Row>> => {
  const dimensions: Array<Dimension<'props', string, Row>> = []
  const { dimension, seriesDimension } = spec

  if (dimension != null) {
    dimensions.push(
      dimension.type === 'date'
        ? { id: dimension.field, label: dimension.label, type: 'date', format: 'MMM d' }
        : {
            id: dimension.field,
            label: dimension.label,
            type: 'string',
            descriptions: buildDimensionDescriptions(dimension.field, rows)
          }
    )
  }
  if (seriesDimension != null) {
    dimensions.push({
      id: seriesDimension.field,
      label: seriesDimension.label,
      type: 'string',
      descriptions: buildDimensionDescriptions(seriesDimension.field, rows)
    })
  } else if (spec.type === 'scatter') {
    dimensions.push(buildScatterSeriesDimension())
  }

  return dimensions
}

type DataChartDimension = NonNullable<DataChartSpec['dimension']>
type FirstAxis = string | ChartDimensionValuesVariable<'props', string, Row>

/**
 * Category dimensions keep the row order (or the requested sort); dates stay chronological,
 * which is the only order `dataChartSpecSchema` lets a date dimension request.
 */
const buildFirstAxis = (dimension: DataChartDimension, sort: DataChartSpec['sort']): FirstAxis => {
  if (dimension.type === 'date') return dimension.field

  return {
    id: 'copilot-dimension',
    type: 'values',
    dimension: dimension.field,
    change: {
      change: 'static',
      options: 'all'
    },
    ...(sort !== 'data' ? { sort } : {})
  }
}

const metricsAxis = (metrics: DataChartMetric[]): SecondAxis =>
  metrics.length === 1 ? metrics[0].field : metrics.map((metric) => metric.field)

const optionalMetricsAxis = (metrics: DataChartMetric[]): SecondAxis | undefined =>
  metrics.length > 0 ? metricsAxis(metrics) : undefined

/** Draws one metric at a time; the others stay reachable through ChartCard's metric picker. */
const singleMetricAxis = (metrics: DataChartMetric[]): string | ChartMetric<'props', string, Row> =>
  metrics.length === 1
    ? metrics[0].field
    : {
        id: 'copilot-metric',
        type: 'metric',
        change: {
          change: 'internal',
          options: metricFields(metrics)
        }
      }

const buildSecondAxis = (spec: DataChartSpec): SecondAxis => {
  if (spec.seriesDimension == null) return metricsAxis(spec.metrics)

  return {
    id: 'copilot-metric',
    type: 'metricTransposed',
    change: {
      change: 'internal',
      options: [spec.metrics[0].field]
    },
    transpose: spec.seriesDimension.field
  }
}

const buildChart = (spec: DataChartSpec): ChartCardChartProps<string, Row> | null => {
  const stackProps = spec.stacked ? { stackId: STACK_ID } : undefined

  if (spec.type === 'scatter') {
    const [xMetric, yMetric] = spec.metrics
    if (yMetric == null) return null
    return {
      type: 'scatter',
      firstAxis: xMetric.field,
      secondAxis: yMetric.field,
      thirdAxis: spec.seriesDimension?.field ?? COPILOT_SCATTER_SERIES_FIELD
    }
  }

  if (spec.dimension == null) return null
  const firstAxis = buildFirstAxis(spec.dimension, ranksCategoriesHere(spec) ? 'data' : spec.sort)

  switch (spec.type) {
    case 'pie':
    case 'donut':
      return {
        type: spec.type,
        firstAxis,
        secondAxis: singleMetricAxis(spec.metrics)
      }
    case 'bar-line': {
      const barLine: CartesianChartBaseDefinition<'props', string, Row, 'bar', 'line'> = {
        type: 'bar',
        otherType: 'line',
        firstAxis,
        secondAxis: metricsAxis(spec.metrics),
        otherSecondAxis: metricsAxis(spec.secondMetrics),
        customProps: stackProps
      }
      return barLine
    }
    case 'line-bar': {
      const lineBar: CartesianChartBaseDefinition<'props', string, Row, 'line', 'bar'> = {
        type: 'line',
        otherType: 'bar',
        firstAxis,
        secondAxis: metricsAxis(spec.metrics),
        otherSecondAxis: metricsAxis(spec.secondMetrics),
        otherCustomProps: stackProps
      }
      return lineBar
    }
    case 'bar': {
      const bar: CartesianChartBaseDefinition<'props', string, Row, 'bar', 'bar'> = {
        type: 'bar',
        firstAxis,
        secondAxis: buildSecondAxis(spec),
        otherSecondAxis: optionalMetricsAxis(spec.secondMetrics),
        customProps: stackProps,
        otherCustomProps: stackProps
      }
      return bar
    }
    case 'line':
      // Recharts lines cannot stack, so `stacked` has nothing to apply to here.
      return {
        type: spec.type,
        firstAxis,
        secondAxis: buildSecondAxis(spec),
        otherSecondAxis: optionalMetricsAxis(spec.secondMetrics)
      }
    case 'gradientLine':
    case 'area': {
      // ChartCard stacks these by default, so `stacked: false` has to clear the stack explicitly.
      const areaStackProps = { stackId: spec.stacked ? STACK_ID : undefined }
      const area: CartesianChartBaseDefinition<'props', string, Row, 'gradientLine' | 'area', 'gradientLine' | 'area'> =
        {
          type: spec.type,
          firstAxis,
          secondAxis: buildSecondAxis(spec),
          otherSecondAxis: optionalMetricsAxis(spec.secondMetrics),
          customProps: areaStackProps,
          otherCustomProps: areaStackProps
        }
      return area
    }
  }
}

/** Returns null only for a spec ChartCard cannot draw; callers show “Chart unavailable”. */
export const mapDataChartToChartCardProps = (
  chart: CopilotDataChart,
  options: { currencyCode?: string }
): ChartCardProps<string, Row> | null => {
  const { spec, sourceCallId } = chart
  const chartDefinition = buildChart(spec)
  if (chartDefinition == null) return null

  const normalizedRows = normalizeRows(chart)
  const rows = ranksCategoriesHere(spec) ? rankRowsByCategoryTotal(normalizedRows, spec) : normalizedRows
  const currencyCode = spec.currency.length > 0 ? spec.currency : options.currencyCode

  return {
    card: {
      id: sourceCallId,
      title: spec.title,
      type: 'chart',
      chart: chartDefinition
    },
    dataDefinition: {
      dimensions: buildDimensions(spec, rows),
      metrics: [...spec.metrics, ...spec.secondMetrics].map(({ field, label, type }) =>
        buildTypedMetric({ id: field, label, type }, currencyCode)
      )
    },
    data: {
      data: rows,
      isLoading: false,
      isError: false
    },
    mutable: {
      selections: {}
    },
    customisations: COPILOT_CHART_CUSTOMISATIONS
  }
}
